/**
 * What a place looks like before there is anything in it, with the one action that fills it (cockatrice-ui
 * `src/ui/EmptyState.jsx`, ported to hyperscript; PLAN.md §0).
 * Say what will be here and how to start, in a calm voice: "Your shelf is empty, for now."
 * size="page" for whole screens, "inline" inside a panel or list.
 * `headingLevel` 1, `headingId` and `headingFocusable` (PLAN.md §3, step 8) let a whole screen's state be its page: the
 * app's session screens name their h1 and move the focus to it.
 */
import './css/empty-state.css';
import Icon from './Icon.ts';
import type { IconName } from './Icon.ts';
import { component, dynamic, el, when } from './solid.ts';
import type { Child } from './solid.ts';

export interface EmptyStateProps {
  /** "Your shelf is empty, for now." */
  readonly title: Child;
  /** One or two sentences on how to start. */
  readonly children?: Child;
  /** Buttons; primary last. */
  readonly actions?: Child;
  /** Icon name, or false. Default "notes". */
  readonly icon?: IconName | false | undefined;
  /** Default "page". */
  readonly size?: 'page' | 'inline' | undefined;
  /** Heading level. Default 2; 1 where the state is the whole page. */
  readonly headingLevel?: 1 | 2 | 3 | 4 | 5 | 6 | undefined;
  /** The heading's id, for a page that describes its buttons by it or names it in tests. */
  readonly headingId?: string | undefined;
  /** `tabindex="-1"` on the heading: script can focus it, Tab never stops there. */
  readonly headingFocusable?: boolean | undefined;
  /** Extra class. */
  readonly class?: string | undefined;
}

export default function EmptyState(props: EmptyStateProps): Child {
  const level = (): string => `h${props.headingLevel ?? 2}`;
  return el(
    'div',
    { class: () => `ui-empty-state ui-empty-state--${props.size ?? 'page'}${props.class !== undefined && props.class !== '' ? ` ${props.class}` : ''}` },
    when(
      () => props.icon !== false,
      () =>
        el(
          'span',
          { class: 'ui-empty-state__icon', 'aria-hidden': 'true' },
          component(Icon, {
            get name() {
              return props.icon === undefined || props.icon === false ? 'notes' : props.icon;
            },
            size: 'lg',
          }),
        ),
    ),
    dynamic(
      level,
      { class: 'ui-empty-state__title', id: () => props.headingId, tabIndex: () => (props.headingFocusable === true ? -1 : undefined) },
      () => props.title,
    ),
    when(
      () => Boolean(props.children),
      () => el('div', { class: 'ui-empty-state__text' }, () => props.children),
    ),
    when(
      () => Boolean(props.actions),
      () => el('div', { class: 'ui-empty-state__actions' }, () => props.actions),
    ),
  );
}
